import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import firebaseService from '../services/firebaseService';
import { Crown, Heart, Star, MapPin, ArrowLeft, Calendar, User } from 'lucide-react';

const Container = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  left: 0;
  top: 0;
  background: #5752e8;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
  transition: background 0.2s;
  
  &:hover {
    background: #5752e8;
  }
  
  @media (max-width: 768px) {
    position: static;
    margin-bottom: 15px;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  background: #5752e8;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  font-size: 0.95rem;
`;

const FilterSection = styled.div`
  background: #F8F4FF;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
  border: 1px solid #E9D5FF;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const FilterTitle = styled.div`
  font-weight: 600;
  color: #231F20;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? '#5752e8' : 'white'};
  color: ${props => props.active ? 'white' : '#5752e8'};
  border: 2px solid ${props => props.active ? '#5752e8' : '#E9D5FF'};
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#5752e8' : '#F8F4FF'};
    border-color: ${props => props.active ? '#5752e8' : '#5752e8'};
  }
`;

const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ReviewCard = styled.div`
  background: ${props => {
    if (props.rank === 1) return '#faaf40';
    if (props.rank === 2) return '#a9a9a9';
    if (props.rank === 3) return '#c56a2e';
    return 'white';
  }};
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
`;

const RankBadge = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  min-width: 40px;
  text-align: center;
  color: ${props => props.rank <= 3 ? 'white' : '#5752e8'};
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    min-width: 30px;
  }
`;

const RestroomInfo = styled.div`
  flex: 1;
`;

const RestroomName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 5px;
  color: ${props => props.rank <= 3 ? 'white' : '#2c3e50'};
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const RestroomDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.9rem;
  opacity: ${props => props.rank <= 3 ? '0.95' : '0.8'};
  color: ${props => props.rank <= 3 ? 'white' : '#555'};
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    gap: 8px;
  }
`;

const Detail = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ReviewContent = styled.div`
  margin-bottom: 15px;
`;

const ReviewText = styled.div`
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 10px;
  color: ${props => props.rank <= 3 ? 'white' : '#2c3e50'};
  font-style: italic;
`;

const ReviewMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  color: ${props => props.rank <= 3 ? 'white' : '#555'};
`;

const Reviewer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const LikesCount = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
`;

const LoadingIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 20px;
  animation: bounce 1s infinite;
  
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.3;
`;

function RoyalReviews() {
  const navigate = useNavigate();
  const { currentUser: user } = useAuth();
  const [topReviews, setTopReviews] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all'); // 'all', 'day', 'week', 'month'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTopReviews();
  }, [timeFilter]);

  const fetchTopReviews = async () => {
    try {
      setLoading(true);
      const reviews = await firebaseService.getTopLikedReviews(timeFilter, 10);
      setTopReviews(reviews);
    } catch (error) {
      console.error('Error fetching top reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <Detail>
        <Star size={14} fill="currentColor" />
        <span>{rating.toFixed(1)}</span>
      </Detail>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString?.toDate?.() || dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const getTimeLabel = () => {
    switch(timeFilter) {
      case 'all': return 'All Time';
      case 'day': return 'Today';
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      default: return 'This Week';
    }
  };

  const getSubtitle = () => {
    return `Most Liked Reviews ${getTimeLabel()} • Fit for Royalty 👑`;
  };

  const handleProfileClick = (event, username) => {
    event.stopPropagation();
    if (!username) return;
    navigate(`/app/profile/${encodeURIComponent(username)}`);
  };

  const handleRestroomClick = async (restroom) => {
    if (restroom) {
      try {
        // Fetch the complete restroom data since we only have partial data from the review
        const completeRestroom = await firebaseService.getRestroomById(restroom.id);
        
        if (completeRestroom) {
          navigate('/app', { state: { selectedRestroom: completeRestroom, zoom: 17 } });
        } else {
          console.error('Could not fetch complete restroom data');
          // Fallback to partial data if complete fetch fails
          navigate('/app', { state: { selectedRestroom: restroom, zoom: 17 } });
        }
      } catch (error) {
        console.error('Error fetching complete restroom:', error);
        // Fallback to partial data if fetch fails
        navigate('/app', { state: { selectedRestroom: restroom, zoom: 17 } });
      }
    }
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <BackButton onClick={() => navigate('/app')}>
            <ArrowLeft size={18} />
            Back to Map
          </BackButton>
          <Title>
            <Crown size={32} />
            Royal Reviews
          </Title>
          <Subtitle>Loading royal content...</Subtitle>
        </Header>
        
        <LoadingState>
          <LoadingIcon>👑</LoadingIcon>
          <h3>Finding the Most Liked Reviews...</h3>
          <p>Crowning the best restroom experiences!</p>
        </LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/')}>
          <ArrowLeft size={18} />
          Back to Map
        </BackButton>
        <Title>
          <Crown size={32} />
          Royal Reviews
        </Title>
        <Subtitle>{getSubtitle()}</Subtitle>
      </Header>

      <FilterSection>
        <FilterTitle>
          <Calendar size={18} />
          Time Period
        </FilterTitle>
        
        <FilterGroup>
          <FilterButton 
            active={timeFilter === 'all'}
            onClick={() => setTimeFilter('all')}
          >
            👑 All Time
          </FilterButton>
          <FilterButton 
            active={timeFilter === 'day'}
            onClick={() => setTimeFilter('day')}
          >
            🌅 Today
          </FilterButton>
          <FilterButton 
            active={timeFilter === 'week'}
            onClick={() => setTimeFilter('week')}
          >
            📊 This Week
          </FilterButton>
          <FilterButton 
            active={timeFilter === 'month'}
            onClick={() => setTimeFilter('month')}
          >
            🗓️ This Month
          </FilterButton>
        </FilterGroup>
      </FilterSection>

      {topReviews.length === 0 ? (
        <EmptyState>
          <EmptyIcon>👑</EmptyIcon>
          <h3>No Royal Reviews Yet!</h3>
          <p>Be the first to write a review that earns the crown!</p>
        </EmptyState>
      ) : (
        <ReviewsList>
          {topReviews.map((review, index) => {
            const rank = index + 1;
            return (
              <ReviewCard 
                key={review.id} 
                rank={rank}
                onClick={() => handleRestroomClick(review.restroom)}
              >
                <ReviewHeader>
                  <RankBadge rank={rank}>
                    {rank === 1 && '👑'}
                    {rank === 2 && '🥈'}
                    {rank === 3 && '🥉'}
                    {rank > 3 && `#${rank}`}
                  </RankBadge>
                  
                  <RestroomInfo rank={rank}>
                    <RestroomName rank={rank}>
                      {review.restroom?.name || 'Unknown Restroom'}
                    </RestroomName>
                    <RestroomDetails rank={rank}>
                      {review.restroom?.address && (
                        <Detail>
                          <MapPin size={14} />
                          <span>{review.restroom.address}</span>
                        </Detail>
                      )}
                      {review.restroom?.category && (
                        <Detail>
                          <span>📍 {review.restroom.category.replace('-', ' ')}</span>
                        </Detail>
                      )}
                      {renderStars(review.rating || 3)}
                    </RestroomDetails>
                  </RestroomInfo>
                </ReviewHeader>
                
                <ReviewContent>
                  <ReviewText rank={rank}>
                    "{review.comment}"
                  </ReviewText>
                </ReviewContent>
                
                <ReviewMeta rank={rank}>
                  <Reviewer
                    rank={rank}
                    role="button"
                    tabIndex={0}
                    onClick={(event) => handleProfileClick(event, review.username)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        handleProfileClick(event, review.username);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <User size={14} />
                    <span>@{review.username}</span>
                  </Reviewer>
                  <LikesCount rank={rank}>
                    <Heart size={14} fill="currentColor" />
                    <span>{review.likes || 0} likes</span>
                  </LikesCount>
                </ReviewMeta>
              </ReviewCard>
            );
          })}
        </ReviewsList>
      )}
    </Container>
  );
}

export default RoyalReviews;
